import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthState, onAuthUIStateChange, CognitoUserInterface } from '@aws-amplify/ui-components';
import { useHistory, useLocation } from 'react-router-dom';
import { AmplifyAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';

import CustomForgotPassword from '../pages/ForgotPassword';
import CustomSignIn from '../pages/SignIn';
import CustomSignUp from '../pages/SignUp';
import CustomConfirmSignUp from '../pages/ConfirmSignUp';

const authStatesToUrl = {
  [AuthState.SignIn]: '/sign-in',
  [AuthState.SignedOut]: '/sign-in',
  [AuthState.ConfirmSignIn]: '/sign-in',
  [AuthState.SignUp]: '/sign-up',
  [AuthState.SignedIn]: '/authenticated',
  [AuthState.ForgotPassword]: '/forgot-password',
  [AuthState.ConfirmSignUp]: '/confirm-sign-up',
} as { [key: string]: string };

const urlToAuthStates = {
  '/sign-in': AuthState.SignIn,
  '/sign-up': AuthState.SignUp,
} as { [key: string]: AuthState.SignIn | AuthState.SignUp | undefined };

interface AuthData extends CognitoUserInterface {
  'custom:account_id': string;
  email: string;
}

interface AuthContextParams {
  authState?: AuthState;
  authData?: AuthData;
  authRefresh?: () => Promise<void>; // Promise<AuthData>;
}

export const AuthContext = createContext<AuthContextParams>({});

export const useAuth = () => useContext(AuthContext);

let signInUsername = '';
let signInPassword = '';

function setUsername(username: string) {
  signInUsername = username;
}

function setPassword(password: string) {
  signInPassword = password;
}

export function AuthProvider({ children }: React.PropsWithChildren<{}>) {
  const history = useHistory();
  const location = useLocation();

  const [{ authData, authState }, setCtxData] = useState<Omit<AuthContextParams, 'authRefresh'>>(
    {},
  );

  const initialAuthState = urlToAuthStates[location.pathname];

  const authRefresh = async () => {
    // forces cognito to refetch the updated user from the user pool
    const refreshedAuthData = await Auth.currentAuthenticatedUser({ bypassCache: true });
    setCtxData({ authData: refreshedAuthData, authState });
  };

  useEffect(
    () =>
      // if the user has just signed in, I want to redirect them to the /authenticated page

      // eslint-disable-next-line consistent-return
      onAuthUIStateChange((nextAuthState, nextAuthData) => {
        // nextAuthData appears to only ever be of type CognitoUserInterface or undefined, seems like 'object' is bad typing
        setCtxData({
          authState: nextAuthState,
          authData: nextAuthData as AuthData | undefined,
        });

        // if the user is not logged in, make sure the url matches the auth state
        const authUrl = authStatesToUrl[nextAuthState] || '';
        if (!nextAuthData && authUrl) return history.push(authUrl);

        // if the user logs in, make sure they get redirected to the authenticated page
        if (location.pathname === '/sign-in' && nextAuthState === AuthState.SignedIn) {
          return history.push('/authenticated');
        }
      }),
    [history, location.pathname],
  );

  return (
    <AuthContext.Provider value={{ authData, authState, authRefresh }}>
      <AmplifyAuthenticator
        className="amplify"
        usernameAlias="email"
        initialAuthState={initialAuthState}
      >
        <CustomSignIn {...{ signInUsername, setUsername, signInPassword, setPassword }} />
        <CustomSignUp {...{ signInUsername, setUsername, signInPassword, setPassword }} />
        <CustomConfirmSignUp {...{ signInUsername, signInPassword }} />
        <CustomForgotPassword />
      </AmplifyAuthenticator>

      {authState === AuthState.SignedIn && authData && children}
    </AuthContext.Provider>
  );
}
